import type { JSX, ReactNode } from "react";

import { Toast as ArkToast, toastAnatomy } from "@ark-ui/react/toast";
import { IconAlertCircle } from "@mobsuccess-devops/icons/alert-circle";
import { IconCheckCircle1 } from "@mobsuccess-devops/icons/check-circle-1";
import { IconClose } from "@mobsuccess-devops/icons/close";
import { IconInformationCircle } from "@mobsuccess-devops/icons/information-circle";
import { IconRoadSignWarning } from "@mobsuccess-devops/icons/road-sign-warning";
import { sva, type RecipeVariant } from "@mobsuccess-devops/styled-system/css";
import { hstack } from "@mobsuccess-devops/styled-system/patterns";
import type { Options } from "@zag-js/toast";

import { Button } from "../Button";

type PaletteVariant = RecipeVariant<typeof styles>["palette"];
type TypeVariant = "success" | "error" | "loading" | "info" | "warning";

const icons = {
  info: IconInformationCircle.Bold,
  error: IconAlertCircle.Bold,
  success: IconCheckCircle1.Bold,
  warning: IconRoadSignWarning.Bold,
  loading: IconInformationCircle.Bold, // should not be available / defined for type safety
} satisfies Record<TypeVariant, React.FC>;

type ToastProps = Options<ReactNode>;

function isPalette(type: unknown): type is PaletteVariant {
  return (
    ["success", "danger", "warning", "info"] satisfies PaletteVariant[]
  ).some((palette) => palette === String(type));
}

function toTypeString(type?: unknown): TypeVariant {
  switch (type) {
    case "success":
    case "error":
    case "loading":
    case "info":
    case "warning":
      return type;
    default:
      return "info";
  }
}

export function Toast({
  title,
  actions,
  description,
  type: $type,
  meta,
}: ToastProps): JSX.Element {
  const type = toTypeString(meta?.type ?? $type);
  const classes = styles({
    palette: isPalette(meta?.palette) ? meta.palette : undefined,
    withActions: !!actions,
  });
  const Icon = icons[type];
  return (
    <ArkToast.Root className={classes.root}>
      <Icon
        className={classes.icon}
        color={type === "error" ? "danger" : type}
      />
      <ArkToast.Title className={classes.title}>{title}</ArkToast.Title>
      <ArkToast.Description className={classes.description}>
        {description}
      </ArkToast.Description>
      <ArkToast.CloseTrigger className={classes.closeTrigger}>
        <IconClose.Bold size="8px" color="interface" />
      </ArkToast.CloseTrigger>
      {actions && (
        <div className={classes.actions}>
          {actions.map(({ label, ...props }) => (
            <ArkToast.ActionTrigger key={props.id} asChild>
              <Button type="button" {...props}>
                {label}
              </Button>
            </ArkToast.ActionTrigger>
          ))}
        </div>
      )}
    </ArkToast.Root>
  );
}

const styles = sva({
  slots: toastAnatomy.extendWith("icon", "actions").keys(),
  base: {
    root: {
      display: "grid",
      gap: "xs",
      gridTemplateAreas: `
          "icon title closeTrigger"
          ". description description"
        `,
      gridTemplateColumns: "auto 1fr auto",
      gridTemplateRows: "auto auto",
      opacity: "var(--opacity)",
      zIndex: "var(--z-index)",
      listStyle: "none",
      boxShadow:
        "0 4px 16px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.08)",
      height: "var(--height)",
      width: "260px",
      overflowWrap: "anywhere",
      scale: "var(--scale)",
      translate: "var(--x) var(--y) 0",
      willChange: "translate, opacity, scale",
      transitionDuration: "slow",
      transitionProperty: "translate, scale, opacity, height",
      transitionTimingFunction: "default",
      padding: "sm",
      borderRadius: "md",
      borderLeft: "2px solid token(colors.colorPalette)",
      backgroundColor: "white",
      animation: "slide-in .2s ease-in-out",
      _closed: {
        animation: "slide-out 0.2s ease-in-out",
      },
    },
    icon: {
      alignSelf: "center",
      gridArea: "icon",
      size: "xs",
    },
    title: {
      gridArea: "title",
      color: "colorPalette",
      textStyle: "body",
    },
    actions: {
      ...hstack.raw(),
      gap: "sm",
      justifySelf: "end",
      gridArea: "actions",
      justifyContent: "flex-end",
    },
    description: {
      gridArea: "description",
      color: "interface",
      textStyle: "muted",
    },
    closeTrigger: {
      justifySelf: "end",
      alignSelf: "center",
      gridArea: "closeTrigger",
      outline: "none",
      border: "none",
      cursor: "pointer",
      background: "transparent",
      fill: "interface.300",
      size: "xs",
      transition: "all 0.2s ease-in-out",
      _hover: {
        fill: "interface.400",
        rotate: "90deg",
      },
    },
  },
  variants: {
    palette: {
      success: {
        root: {
          colorPalette: "success",
        },
      },
      danger: {
        root: {
          colorPalette: "danger",
        },
      },
      info: {
        root: {
          colorPalette: "info",
        },
      },
      warning: {
        root: {
          colorPalette: "warning",
        },
      },
    },
    withActions: {
      true: {
        root: {
          gridTemplateAreas: `
              "icon title closeTrigger"
              ". description description"
              ". actions actions"
            `,
          gridTemplateRows: "auto auto auto",
        },
      },
    },
  },
  defaultVariants: {
    palette: "info",
    withActions: false,
  },
});
